import React, { useEffect } from 'react';
import { ExitCodes, ModalMessage, ModalMessageFailure, modalResolver } from '../../helpers/modal/modal.helper';
import Message from '../../components/message/Message';
import { isUnified } from '../../helpers/cookies/unified.helper';
import { Application, ChannelTypes, Dealer } from '../../gql/graphql';
import { SegmentProps } from '../../state-machine/actions/segment';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

type DeclinedProps = SegmentProps & {
  orderId: string;
  dealer: Dealer;
  application: Application | undefined;
  heading?: string;
  body?: string | JSX.Element;
  additionalBody?: string;
  trackDetails?: { title: string; details?: any };
  setHeaderGoBackFunc?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
};

// Now that an app can be declined after prequal, the wording of the message differs depending on
// when the decline happened.  Presently only bank account validation
// TODO: I don't really love checking/handling specific decline reasons, is there a better way to determine
//       if the decline occurred before/after prequal?
const getDefaultHeading = (declineReason: string | undefined, t: TFunction) => {
  switch (declineReason) {
    case 'bankAccount':
    case 'debitCardError': // Does not work as decline reason is actually empty in this case...but why
      return t(($) => $.pageDeclined.unableToApprove);
    default:
      return t(($) => $.pageDeclined.unableToPrequalify);
  }
};

const Declined: React.FC<DeclinedProps> = ({ orderId, dealer, application, heading, body, additionalBody, trackDetails, page, track, setHeaderGoBackFunc }) => {
  const { t } = useTranslation();
  const domains = dealer.domains;
  const isVirtualCard = dealer.isVirtualCard;
  const channelType = dealer.channelType;
  const leaseDeclineReason = application?.lease?.declineReason;
  const isUnifiedUi = isUnified(); // TODO: remove and move to SM context

  useEffect(() => {
    setHeaderGoBackFunc?.(undefined);

    const sendDeclinedEvent = () => {
      const payload: ModalMessage = {
        wcfSuccess: false,
        closeModal: false,
        payload: {
          orderId: orderId,
          reasonCode: ExitCodes.hardDecline,
          reason: 'hardDecline',
        } as ModalMessageFailure,
      };

      modalResolver(payload, domains);
    };
    page('Declined');

    if (!!trackDetails) {
      track(trackDetails.title, trackDetails.details);
    }

    // having this wait for 5 seconds so partners don't immediately close the modal
    setTimeout(() => {
      sendDeclinedEvent();
    }, 5000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseModal = () => {
    const payload: ModalMessage = {
      wcfSuccess: false,
      closeModal: true,
      payload: {
        reason: 'modal closed',
        reasonCode: ExitCodes.generalCloseModal,
        orderId: orderId,
      } as ModalMessageFailure,
    };
    modalResolver(payload, domains);
  };

  // Dont show close button for virtual card, B&M or loans because it will do nothing
  const shouldShowButton = !isVirtualCard && isUnifiedUi && channelType !== ChannelTypes.BrickAndMortar;
  return (
    <Message
      heading={heading || getDefaultHeading(leaseDeclineReason ?? undefined, t)}
      body={body || t(($) => $.pageDeclined.applyAgain)}
      additionalBody={additionalBody}
      buttonText={t(($) => $.buttonLabels.exit)}
      buttonClick={handleCloseModal}
      shouldShowButton={shouldShowButton}
      data-testid="decision-declined"
    />
  );
};

export default Declined;
